import { CrearRequisitoModal } from '@/components/sysadmin/CrearRequisitoModal';
import { Badge, Button, Card, Input, LoadingContainer, Modal, Select } from '@/components/ui';
import { useEmpresasSysadmin, useNtcsDisponibles, useRequisitosEmpresa } from '@/hooks/useSysadmin';
import type { EmpresaSysadmin, RequisitoEmpresaEstado } from '@/services/sysadmin.service';
import { AlertTriangle, BookOpen, Building2, CheckSquare, Link2Off, Minus, Plus, Search, Square, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { EditarRequisitosPage } from './EditarRequisitosPage';

// ========================================
// Componente: Selector de empresa
// ========================================

function EmpresaSelector({
  empresas,
  selected,
  onSelect,
}: {
  empresas: EmpresaSysadmin[];
  selected: EmpresaSysadmin | null;
  onSelect: (e: EmpresaSysadmin) => void;
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return empresas;
    const q = search.toLowerCase();
    return empresas.filter((e) => e.nombre.toLowerCase().includes(q) || e.nitId.toLowerCase().includes(q));
  }, [empresas, search]);

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Buscar empresa por nombre o NIT..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-80 overflow-y-auto">
        {filtered.map((empresa) => (
          <button
            type="button"
            key={empresa.nitId}
            onClick={() => onSelect(empresa)}
            className={`text-left p-3 rounded-lg border transition-all ${
              selected?.nitId === empresa.nitId
                ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <p className="font-medium text-sm text-gray-900 truncate">{empresa.nombre}</p>
            <p className="text-xs text-gray-500">NIT: {empresa.nitId}</p>
            {empresa.combinacionNtc && <p className="text-xs text-blue-600 mt-1">{empresa.combinacionNtc.nombre}</p>}
          </button>
        ))}
      </div>
      {filtered.length === 0 && <p className="text-gray-400 text-sm text-center py-4">No se encontraron empresas</p>}
    </div>
  );
}

// ========================================
// Componente: Tabla de requisitos
// ========================================

function getCalificacionBadge(cal: string | null) {
  switch (cal) {
    case 'C':
      return <Badge variant="green">C</Badge>;
    case 'NC':
      return <Badge variant="red">NC</Badge>;
    case 'CP':
      return <Badge variant="yellow">CP</Badge>;
    case 'NA':
      return <Badge variant="gray">NA</Badge>;
    default:
      return <span className="text-gray-400 text-xs">—</span>;
  }
}

interface RequisitosTableProps {
  requisitos: RequisitoEmpresaEstado[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onAsociar: (id: string) => void;
  onDesasociar: (id: string) => void;
  saving: string | null;
}

function RequisitosTable({
  requisitos,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onDeselectAll,
  onAsociar,
  onDesasociar,
  saving,
}: RequisitosTableProps) {
  const allSelected = requisitos.length > 0 && selectedIds.size === requisitos.length;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm table-fixed" style={{ minWidth: '700px' }}>
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-3 py-2 text-center w-[5%]">
              <button type="button" onClick={allSelected ? onDeselectAll : onSelectAll} className="text-gray-400 hover:text-gray-600">
                {allSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
              </button>
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-[12%]">NTC</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-[10%]">Numeral</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-[43%]">Descripción</th>
            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-[13%]">Estado</th>
            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-[8%]">Calif.</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-[9%]">Acción</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {requisitos.map((req) => (
            <tr key={req.requisitoId} className={`hover:bg-gray-50 ${!req.asociado ? 'opacity-60' : ''}`}>
              <td className="px-3 py-2 text-center">
                <button type="button" onClick={() => onToggleSelect(req.requisitoId)} className="text-gray-400 hover:text-gray-600">
                  {selectedIds.has(req.requisitoId) ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4" />}
                </button>
              </td>
              <td className="px-3 py-2 overflow-hidden">
                <span className="text-xs font-medium text-gray-700 wrap-break-word">{req.ntcNombre}</span>
              </td>
              <td className="px-3 py-2 overflow-hidden">
                <span className="text-gray-900 font-mono text-xs wrap-break-word break-all">
                  {req.capitulo}.{req.numeral}
                </span>
              </td>
              <td className="px-3 py-2 overflow-hidden">
                <p className="text-gray-700 wrap-break-word whitespace-pre-line">{req.descripcion}</p>
              </td>
              <td className="px-3 py-2 text-center">
                {req.asociado ? <Badge variant="green">Asociado</Badge> : <Badge variant="gray">No asociado</Badge>}
              </td>
              <td className="px-3 py-2 text-center">{getCalificacionBadge(req.calificacion)}</td>
              <td className="px-3 py-2 text-right">
                {req.asociado ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDesasociar(req.requisitoId)}
                    disabled={saving === req.requisitoId}
                    title="Desasociar"
                    className="text-red-500 hover:text-red-700"
                  >
                    {saving === req.requisitoId ? <span className="text-xs">...</span> : <X className="w-4 h-4" />}
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onAsociar(req.requisitoId)}
                    disabled={saving === req.requisitoId}
                    title="Asociar"
                    className="text-green-500 hover:text-green-700"
                  >
                    {saving === req.requisitoId ? <span className="text-xs">...</span> : <Plus className="w-4 h-4" />}
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ========================================
// Componente: Panel de requisitos de una empresa
// ========================================

function RequisitosPanel({ empresa }: { empresa: EmpresaSysadmin }) {
  const { ntcs } = useNtcsDisponibles();
  const [selectedNtcId, setSelectedNtcId] = useState<number | undefined>(undefined);
  const [filterAsociado, setFilterAsociado] = useState<'todos' | 'asociados' | 'no-asociados'>('todos');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [savingId, setSavingId] = useState<string | null>(null);
  const [batchSaving, setBatchSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [batchAction, setBatchAction] = useState<'asociar' | 'desasociar'>('asociar');

  const { requisitos, isLoading, error, asociarRequisito, desasociarRequisito, asociarBatch, desasociarBatch } = useRequisitosEmpresa(
    empresa.nitId,
    selectedNtcId
  );

  const filteredRequisitos = useMemo(() => {
    let result = requisitos;

    if (filterAsociado === 'asociados') {
      result = result.filter((r) => r.asociado);
    } else if (filterAsociado === 'no-asociados') {
      result = result.filter((r) => !r.asociado);
    }

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.descripcion.toLowerCase().includes(q) ||
          r.numeral.toLowerCase().includes(q) ||
          r.capitulo.toLowerCase().includes(q) ||
          r.ntcNombre.toLowerCase().includes(q)
      );
    }

    return result;
  }, [requisitos, filterAsociado, search]);

  const stats = useMemo(() => {
    const total = requisitos.length;
    const asociados = requisitos.filter((r) => r.asociado).length;
    const noAsociados = total - asociados;
    return { total, asociados, noAsociados };
  }, [requisitos]);

  const handleAsociar = async (requisitoId: string) => {
    setSavingId(requisitoId);
    try {
      await asociarRequisito(requisitoId);
    } catch (err) {
      console.error('Error al asociar:', err);
    } finally {
      setSavingId(null);
    }
  };

  const handleDesasociar = async (requisitoId: string) => {
    setSavingId(requisitoId);
    try {
      await desasociarRequisito(requisitoId);
    } catch (err) {
      console.error('Error al desasociar:', err);
    } finally {
      setSavingId(null);
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedIds(new Set(filteredRequisitos.map((r) => r.requisitoId)));
  };

  const handleDeselectAll = () => {
    setSelectedIds(new Set());
  };

  const openBatchConfirm = (action: 'asociar' | 'desasociar') => {
    setBatchAction(action);
    setShowConfirmModal(true);
  };

  const handleBatchAction = async () => {
    setBatchSaving(true);
    try {
      const ids = Array.from(selectedIds);
      if (batchAction === 'asociar') {
        await asociarBatch(ids);
      } else {
        await desasociarBatch(ids);
      }
      setSelectedIds(new Set());
      setShowConfirmModal(false);
    } catch (err) {
      console.error('Error en acción batch:', err);
    } finally {
      setBatchSaving(false);
    }
  };

  if (isLoading) return <LoadingContainer text="Cargando requisitos..." />;
  if (error) return <p className="text-red-600 text-center py-8">{error}</p>;

  return (
    <div className="space-y-4">
      {/* Info empresa */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
        <div>
          <p className="font-semibold text-blue-900">{empresa.nombre}</p>
          <p className="text-sm text-blue-700">NIT: {empresa.nitId}</p>
        </div>
        <div className="flex gap-4 text-sm">
          <div className="text-center">
            <p className="font-bold text-blue-900">{stats.total}</p>
            <p className="text-xs text-blue-600">Total</p>
          </div>
          <div className="text-center">
            <p className="font-bold text-green-700">{stats.asociados}</p>
            <p className="text-xs text-green-600">Asociados</p>
          </div>
          <div className="text-center">
            <p className="font-bold text-gray-500">{stats.noAsociados}</p>
            <p className="text-xs text-gray-400">Sin asociar</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Buscar requisito..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select
          value={selectedNtcId?.toString() || ''}
          onChange={(e) => {
            setSelectedNtcId(e.target.value ? parseInt(e.target.value) : undefined);
            setSelectedIds(new Set());
          }}
          options={[{ value: '', label: 'Todas las NTCs' }, ...ntcs.map((n) => ({ value: n.ntcId.toString(), label: n.nombre }))]}
        />
        <Select
          value={filterAsociado}
          onChange={(e) => {
            setFilterAsociado(e.target.value as 'todos' | 'asociados' | 'no-asociados');
            setSelectedIds(new Set());
          }}
          options={[
            { value: 'todos', label: 'Todos' },
            { value: 'asociados', label: 'Solo asociados' },
            { value: 'no-asociados', label: 'Solo sin asociar' },
          ]}
        />
      </div>

      {/* Acciones batch */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2">
          <span className="text-sm font-medium text-yellow-800">{selectedIds.size} seleccionados</span>
          <div className="flex gap-2 ml-auto">
            <Button variant="secondary" size="sm" onClick={handleDeselectAll}>
              <Minus className="w-3 h-3 mr-1" />
              Deseleccionar
            </Button>
            <Button size="sm" onClick={() => openBatchConfirm('asociar')} className="bg-green-600 hover:bg-green-700 text-white">
              <Plus className="w-3 h-3 mr-1" />
              Asociar
            </Button>
            <Button size="sm" onClick={() => openBatchConfirm('desasociar')} className="bg-red-600 hover:bg-red-700 text-white">
              <Link2Off className="w-3 h-3 mr-1" />
              Desasociar
            </Button>
          </div>
        </div>
      )}

      {/* Tabla */}
      <Card>
        {filteredRequisitos.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400">No se encontraron requisitos con los filtros actuales</p>
          </div>
        ) : (
          <RequisitosTable
            requisitos={filteredRequisitos}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            onAsociar={handleAsociar}
            onDesasociar={handleDesasociar}
            saving={savingId}
          />
        )}
      </Card>

      <p className="text-xs text-gray-400 text-right">{filteredRequisitos.length} requisitos mostrados</p>

      {/* Modal confirmación batch */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={batchAction === 'asociar' ? 'Asociar requisitos' : 'Desasociar requisitos'}
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className={`w-5 h-5 mt-0.5 ${batchAction === 'desasociar' ? 'text-red-500' : 'text-yellow-500'}`} />
            <div>
              <p className="text-sm text-gray-700">
                {batchAction === 'asociar'
                  ? `¿Estás seguro de asociar ${selectedIds.size} requisito${selectedIds.size === 1 ? '' : 's'} a ${empresa.nombre}?`
                  : `¿Estás seguro de desasociar ${selectedIds.size} requisito${selectedIds.size === 1 ? '' : 's'} de ${empresa.nombre}?`}
              </p>
              {batchAction === 'desasociar' && (
                <p className="text-xs text-red-600 mt-1">Se perderán las calificaciones actuales de los requisitos desasociados.</p>
              )}
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleBatchAction}
              disabled={batchSaving}
              className={batchAction === 'desasociar' ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
            >
              {batchSaving ? 'Procesando...' : batchAction === 'asociar' ? 'Asociar' : 'Desasociar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ========================================
// ========================================
// Página principal
// ========================================

type RequisitoViewMode = 'por-empresa' | 'catalogo';

export function GestionRequisitosPage() {
  const { empresas, isLoading, error } = useEmpresasSysadmin();
  const [selectedEmpresa, setSelectedEmpresa] = useState<EmpresaSysadmin | null>(null);
  const [showCrearRequisitoModal, setShowCrearRequisitoModal] = useState(false);
  const [viewMode, setViewMode] = useState<RequisitoViewMode>('por-empresa');

  const handleRequisitoCreated = () => {
    setShowCrearRequisitoModal(false);
  };

  return (
    <div className="space-y-4">
      {/* View mode toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg w-fit">
          <button
            type="button"
            onClick={() => setViewMode('por-empresa')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              viewMode === 'por-empresa'
                ? 'bg-white text-blue-700 shadow-sm ring-1 ring-gray-200'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
            }`}
          >
            <Building2 className="w-4 h-4" />
            Por Empresa
          </button>
          <button
            type="button"
            onClick={() => setViewMode('catalogo')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              viewMode === 'catalogo'
                ? 'bg-white text-blue-700 shadow-sm ring-1 ring-gray-200'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Catálogo Global
          </button>
        </div>
        <Button onClick={() => setShowCrearRequisitoModal(true)} className="bg-blue-600 hover:bg-blue-700">
          + Crear Requisito
        </Button>
      </div>

      {/* Content based on view mode */}
      {viewMode === 'catalogo' ? (
        <EditarRequisitosPage />
      ) : (
        <>
          {isLoading ? (
            <LoadingContainer text="Cargando empresas..." />
          ) : error ? (
            <p className="text-red-600 text-center py-8">{error}</p>
          ) : !selectedEmpresa ? (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Requisitos por Empresa</h2>
                <p className="text-sm text-gray-500">Elige la empresa a la que deseas gestionar los requisitos asociados</p>
              </div>
              <EmpresaSelector empresas={empresas} selected={selectedEmpresa} onSelect={setSelectedEmpresa} />
            </div>
          ) : (
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => setSelectedEmpresa(null)}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 transition-colors"
              >
                ← Cambiar empresa
              </button>
              <RequisitosPanel empresa={selectedEmpresa} />
            </div>
          )}
        </>
      )}

      <CrearRequisitoModal
        isOpen={showCrearRequisitoModal}
        onClose={() => setShowCrearRequisitoModal(false)}
        onSuccess={handleRequisitoCreated}
      />
    </div>
  );
}
